<template>
  <div class="flex flex-col sm:flex-row w-screen h-screen relative bg-gray-50">
    <div class="hidden sm:block">
      <BaseSidebar />
    </div>

    <div class="block sm:hidden">
      <BaseMobileTopBar />
    </div>

    <div class="h-full w-full flex flex-col flex-1 overflow-y-scroll">
      <div class="border-b w-full flex flex-wrap space-y-2 p-4 justify-between">
        <div class="flex gap-x-1">
          <span
            class="flex items-center cursor-pointer"
            @click="$router.push({ name: 'businesses' })"
          >
            Business
          </span>
          <span class="flex items-center">
            <Icon name="ic:baseline-keyboard-arrow-right" />
          </span>
          <span class="flex items-center gap-2 text-primary-500 font-bold">
            <img
              v-if="business?.businessLogo"
              :src="business.businessLogo"
              alt="Business Logo"
              class="h-7 w-7 rounded-md object-cover border border-gray-200"
            >
            <span
              v-else-if="business?.name"
              class="h-7 w-7 rounded-md bg-primary-50 text-primary text-xs font-bold flex items-center justify-center shrink-0"
            >
              {{ businessInitials }}
            </span>
            {{ business?.name }}
          </span>
        </div>

        <slot name="createNoteAndTaskButtons">
          <div class="flex gap-2 items-center">
            <button
              v-if="business?.status"
              v-tooltip.bottom="canEditStatus ? 'Change listing status' : ''"
              class="flex items-center gap-1 rounded-full px-3 py-1.5 text-white text-sm"
              :class="canEditStatus ? 'cursor-pointer' : 'cursor-default'"
              :style="{ backgroundColor: statusBadgeColor(business.status) }"
              :disabled="statusUpdating"
              @click="canEditStatus && statusMenu?.toggle($event)"
            >
              {{ business.status }}
              <Icon
                v-if="statusUpdating"
                name="mdi:loading"
                class="animate-spin"
                size="14"
              />
              <Icon
                v-else-if="canEditStatus"
                name="mdi:chevron-down"
                size="14"
              />
            </button>
            <Menu
              ref="statusMenu"
              :model="statusMenuItems"
              :popup="true"
            />

            <NoteCreate
              v-model:business-id="businessId"
            />

            <TaskCreate
              v-model:business-id="businessId"
            />
          </div>
        </slot>
      </div>

      <div
        v-if="!isLoading"
        class="flex flex-row w-full h-screen h-full overflow-y-hidden"
      >
        <BusinessDetailSideBar
          v-model:business="business"
          v-model:builders="builders"
        />

        <div
          class="h-full w-full flex flex-col flex-1 bg-gray-100 overflow-y-auto"
        >
          <div class="p-6 rounded-md contentContainer">
            <slot />
          </div>
        </div>
      </div>
      <div
        v-else
        class="w-full flex items-center justify-center"
      >
        <ProgressSpinner />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
const route = useRoute()

const isLoading = ref(true)
const businessId = computed(() => {
  return route.params?.businessId?.toString()
})
const builders = ref([])
const business = ref()
const { getAllBuildersByBusinessId } = useBuilder()
const { getBusinessById } = useBusiness()
onMounted(async () => {
  await loadBusiness()
})
watch(businessId, async () => {
  await loadBusiness()
})

const loadBusiness = async () => {
  isLoading.value = true
  const res = await getBusinessById(businessId.value)
  business.value = res

  const builderRes = await getAllBuildersByBusinessId(businessId.value)
  builders.value = builderRes.data
  isLoading.value = false
}

const toast = useToast()

const statusMenu = ref()
const statusUpdating = ref(false)

const businessInitials = computed(() => {
  const name = business.value?.name?.trim()
  if (!name) return '?'
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((word: string) => word[0])
    .join('')
    .toUpperCase()
})

const statusBadgeColor = (status: string) => {
  switch (status) {
    case 'PROSPECT':
      return 'var(--statusProspect)'
    case 'LIVE':
      return 'var(--statusLive)'
    case 'WITHDRAWN':
      return 'var(--statusWithdrawn)'
    case 'SOLD':
      return 'var(--statusSold)'
    default:
      return 'var(--p-gray-500)'
  }
}

// Opportunities are converted via their own flow, not a status flip
const canEditStatus = computed(() => business.value?.status && business.value.status !== 'OPPORTUNITY')

const EDITABLE_STATUSES = ['PROSPECT', 'LIVE', 'WITHDRAWN', 'SOLD', 'LOST']

const statusMenuItems = computed(() =>
  EDITABLE_STATUSES
    .filter(status => status !== business.value?.status)
    .map(status => ({
      label: status,
      command: () => changeStatus(status),
    })),
)

const changeStatus = async (status: string) => {
  statusUpdating.value = true
  try {
    await $fetch(`/api/v1/business/${businessId.value}`, {
      method: 'PUT',
      body: { status },
    })
    business.value.status = status
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: `Listing status changed to ${status}`,
      life: 3000,
    })
  }
  catch (error) {
    console.error('Error updating listing status:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to update listing status',
      life: 3000,
    })
  }
  finally {
    statusUpdating.value = false
  }
}
</script>
